import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { FormDivider, FormParagraph, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { TpcV1DiagnosticReportSummary } from './TpcV1DiagnosticReportSummary';

export function TpcV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    props.diagnosticReport.performerOrganization?.name
                        ? props.diagnosticReport.performerOrganization?.name
                        : undefined
                }
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <TpcV1DiagnosticReportSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <FormDivider />

                {props.diagnosticReport.customControlData.diagnose_COPD_text && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Beschrijving COPD</FormLabel>
                        <InputContainer>
                            <FormParagraph>{props.diagnosticReport.customControlData.diagnose_COPD_text}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.customControlData.diagnose_astma_text && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Beschrijving astma</FormLabel>
                        <InputContainer>
                            <FormParagraph>
                                {props.diagnosticReport.customControlData.diagnose_astma_text}
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <Icd10SelectionsRead
                    diagnosis={props.diagnosticReport.diagnosis}
                    diagnosisConclusion={props.diagnosticReport.customControlData?.conclusie_diagnose}
                    hideTitle
                    showWorkingDiagnosis={
                        props.diagnosticReport.customControlData.diagnose_COPD_value !== 'Anders' &&
                        props.diagnosticReport.customControlData.diagnose_astma_value !== 'Anders'
                    }
                    thesaurusTermDate={props.diagnosticReport.dateSent}
                />

                {props.diagnosticReport.diagnosisRemarks && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosis.diagnosisRemarks')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.diagnosticReport.diagnosisRemarks}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.customControlData.advies &&
                    props.diagnosticReport.customControlData.bezoek_aan_specialist_nodig !== 'Zie advies' && (
                        <HorizontalFormGroupRead>
                            <FormLabel>{translateKey('diagnosticReport.advice')}</FormLabel>
                            <InputContainer>
                                <FormParagraph>{props.diagnosticReport.customControlData.advies}</FormParagraph>
                            </InputContainer>
                        </HorizontalFormGroupRead>
                    )}

                {props.diagnosticReport.customControlData.aanvullende_vragen && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Aanvullende vragen</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                {props.diagnosticReport.customControlData.aanvullende_vragen}
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.diagnosticReport.remark && (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('diagnosticReport.generalRemark')}</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.diagnosticReport.remark}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
            </CustomControlCollapseBody>

            {checkIsFooterVisible() && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideConsultReport')}
                    labelMore={translateKey('diagnosticReport.showConsultReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
            <DiagnosticReportReadButtons {...props} />
        </ProcessContainer>
    );

    function checkIsFooterVisible() {
        const customControlData = props.diagnosticReport.customControlData;

        return Boolean(
            customControlData.diagnose_COPD_text ||
                customControlData.diagnose_astma_text ||
                (!(
                    customControlData.diagnose_COPD_value === 'Anders' ||
                    customControlData.diagnose_astma_value === 'Anders'
                ) &&
                    props.diagnosticReport.diagnosis?.diagnosisConsiderationIcd10) ||
                props.diagnosticReport.diagnosis?.diagnosisDifferentialConsiderationIcd10a ||
                props.diagnosticReport.diagnosis?.diagnosisDifferentialConsiderationIcd10b ||
                props.diagnosticReport.diagnosis?.diagnosisDifferentialConsiderationIcd10c ||
                props.diagnosticReport.diagnosisRemarks ||
                (customControlData.advies && customControlData.bezoek_aan_specialist_nodig !== 'Zie advies') ||
                customControlData.aanvullende_vragen ||
                props.diagnosticReport.remark
        );
    }
}
